import { useToast } from '@melio/penny';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { useNavigate } from '@melio/platform-utils';
import { useSubscriptionMe } from '@melio/subscriptions-api';
import { Navigate } from 'react-router-dom';

import { DEFAULT_DASHBOARD_ROUTE, SubscriptionTabsEnum } from '../../constants';
import { useSubscriptionPlanTier } from '../../utils';
import { WizardLoader } from './components/WizardLoader';
import { useCurrentTab, useWizardParams } from './hooks';
import { SubscriptionWizardScreen } from './screens/SubscriptionWizard.screen';

export const SubscriptionWizardActivity = () => {
  const [isPlatformSubscriptionsEnabled] = useFeature(FeatureFlags.PlatformSubscriptions, false);
  const navigate = useNavigate();
  const { data: subscription, create: createSubscription } = useSubscriptionMe({
    enabled: isPlatformSubscriptionsEnabled,
  });

  const { firstPaidPlan, freePlan } = useSubscriptionPlanTier();

  const { formatMessage } = useMelioIntl();
  const toast = useToast();
  const { currentTab } = useCurrentTab();
  const { returnUrl, planId } = useWizardParams({ firstPaidPlan });

  const onNewUserFreePlanSubscribe = (planId: string) => {
    void createSubscription({ fundingSourceId: '-1', planId, numberOfSeats: -1 })
      .then(() => navigate(returnUrl || DEFAULT_DASHBOARD_ROUTE))
      .catch(() =>
        toast({ type: 'error', title: formatMessage('activities.subscription.checkout.summary.toast.error') })
      );
  };

  const onClose = () => {
    if (!subscription?.planId) {
      onNewUserFreePlanSubscribe(freePlan?.id || '');
    } else {
      navigate(returnUrl || DEFAULT_DASHBOARD_ROUTE);
    }
  };
  const onBack = () => onChangeTab(SubscriptionTabsEnum.Plans);

  if (!planId) {
    return <WizardLoader />;
  }

  if (!isPlatformSubscriptionsEnabled) {
    return <Navigate to="/error" />;
  }

  const onChangeTab = (tab: string) =>
    navigate({ pathname: tab, search: `planId=${planId}` }, { state: { returnUrl } });

  return (
    <SubscriptionWizardScreen
      returnUrl={returnUrl}
      planId={planId}
      currentTab={currentTab}
      onClose={onClose}
      onBack={onBack}
      onChangeTab={onChangeTab}
    />
  );
};
